// GA4 Ecommerce Tracking Component

/**
 * Push GA4 ecommerce events to dataLayer
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

export const EcommerceTracking = {
  
  /**
   * Track product detail view (view_item event)
   * Called when user lands on product page
   * 
   * @param {Object} product - Product data
   * @param {string} product.id - Product UUID
   * @param {string} product.name - Product title
   * @param {number} product.price - Product price (numeric)
   * @param {string} product.category - Product category name
  * @param {string} [product.currency='USD'] - Currency code
   */
  trackViewItem(product) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: product.currency || 'USD',
        value: product.price,
        items: [{
          item_id: product.id,
          item_name: product.name,
          item_category: product.category,
          price: product.price,
          quantity: 1
        }]
      }
    });
    console.log('[GA4 Ecommerce] view_item:', product.id, product.name);
  },

  /**
   * Track add to cart / begin checkout
   * Called when user clicks "Buy Now" button (opens payment modal)
   * 
   * @param {Object} product - Product data
   * @param {string} product.id - Product UUID
   * @param {string} product.name - Product title
   * @param {number} product.price - Product price (numeric)
   * @param {string} product.category - Product category name
  * @param {string} [product.currency='USD'] - Currency code
   */
  trackBeginCheckout(product) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency: product.currency || 'USD',
        value: product.price,
        items: [{
          item_id: product.id,
          item_name: product.name,
          item_category: product.category,
          price: product.price,
          quantity: 1
        }]
      }
    });
    console.log('[GA4 Ecommerce] begin_checkout:', product.id, product.name);
  },

  /**
   * Track successful purchase
   * Called after payment is captured successfully
   * 
   * @param {Object} data - Purchase data
   * @param {string} data.transactionId - Order ID / PayPal transaction ID
   * @param {Object} data.product - Product data
   * @param {string} data.product.id - Product UUID
   * @param {string} data.product.name - Product title
   * @param {number} data.product.price - Product price (numeric)
   * @param {string} data.product.category - Product category name
  * @param {string} [data.currency='USD'] - Currency code
   * @param {number} [data.tax=0] - Tax amount
   * @param {number} [data.shipping=0] - Shipping cost
   */
  trackPurchase(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: data.transactionId,
        value: data.product.price,
        tax: data.tax || 0,
        shipping: data.shipping || 0,
        currency: data.currency || 'USD',
        items: [{
          item_id: data.product.id,
          item_name: data.product.name,
          item_category: data.product.category,
          price: data.product.price,
          quantity: 1
        }]
      }
    });
    console.log('[GA4 Ecommerce] purchase:', data.transactionId, data.product.name);
  },

  /**
   * Helper: Parse price string to number
   * Handles formats like "CHF 29.00", "$29", "29.00"
   * 
   * @param {string|number} price - Price value
   * @returns {number} Numeric price
   */
  parsePrice(price) {
    if (typeof price === 'number') return price;
    if (!price) return 0;
    // Remove currency symbols and extract number
    const match = String(price).match(/[\d.]+/);
    return match ? parseFloat(match[0]) : 0;
  }
};

// Expose to window for inline scripts
if (typeof window !== 'undefined') {
  window.EcommerceTracking = EcommerceTracking;
}
